import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSwivuBryntumContext } from "./useSwivuBryntumContext";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { KANBAN_FIELD_CONFIG } from "@/modern/constants/kanbanConstants";
import { useSwivuStore } from "./useSwivuStore";
import { KanbanColumnsPersistPayload, KanbanColumnType } from "@/modern/components/ui/kanban";
import { STORE_GROUPERS } from "@/other/utils/otherUtils";
import useViews from "./useViews";

const UNGROUPED_COLUMN_ID = "swivu-kanban-ungrouped-column";
const NULL_VALUE = null;

const getGroupHeaderTitle = (header: any) => {
  return header?.name ?? header?.tagValue ?? undefined;
};

/**
 * Creates a synthetic group record that mimics a grid group header record.
 * This allows the kanban column to use the same processCellMenuItems logic as the grid.
 */
export const createSyntheticGroupRecord = (
  column: KanbanColumnType<any>,
  grid: any,
  groupField: string,
  isCollapsed: boolean
) => {
  // Get the actual column instance from grid's column store
  const columnInstance = grid?.columns?.get(groupField);

  // Get the records in this column - these are already Bryntum Model instances
  const groupChildren = column.data || [];

  // Create a synthetic record that exactly mimics a Bryntum grid group header
  const syntheticRecord: any = {
    // Critical: This MUST be true for group header logic
    isGroupHeader: true,

    // All records in this group (can be empty array)
    groupChildren: groupChildren,

    // The column config that this group is for
    swivuGroupColumn: columnInstance,

    // Metadata about the group
    meta: {
      collapsed: isCollapsed,
      groupRowFor: column.title // This is used by the grid for group header identification
    },

    // The group value (for tag selector logic)
    [groupField]: column.value,

    // Additional standard properties
    id: column.id,
    _groupValue: column.value,

    // Methods that might be called
    get: function(field: string) {
      return this[field];
    }
  };

  return syntheticRecord;
};

export const useSwivuKanban = ({ gridRef, storeId, entityType }: { gridRef?: React.RefObject<BryntumGrid>; storeId: string; entityType: string }) => {
  if (!storeId || !entityType) throw new Error("Provide an entity type & entity store id");

  const [isKanbanMode, setIsKanbanMode] = useState(false);
  const [storeUpdateTrigger, setStoreUpdateTrigger] = useState(0);
  const [columns, setColumns] = useState<KanbanColumnType<any>[] | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Record<string, boolean>>({});

  const { project } = useSwivuBryntumContext();
  const { version: storeVersion } = useSwivuStore({ storeId });
  const entityTypeValue = entityType as (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];
  const kanbanFieldConfig = KANBAN_FIELD_CONFIG[entityTypeValue] || [];
  const viewManagerState = useViews();
  const previousViewManagerStateRef = useRef<any>(null);
  const storeVersionRef = useRef(storeVersion);

  const getActiveConfig = useMemo(
    () => (currentStore: any) => {
      if (!currentStore?.groupers) return null;
      return kanbanFieldConfig.find((config) => currentStore.groupers.some((grouper: any) => grouper.field === config.grouperField));
    },
    [kanbanFieldConfig]
  );

  const store = useMemo(() => {
    if (!project) return null;
    return project.getCrudStore(storeId);
  }, [storeId, project]);

  const getActiveGroupOrder = useCallback(
    (grouperField: string) => {
      if (!store?.groupers) return null;
      const grouper = store.groupers.find((g: any) => g.field === grouperField);
      if (!grouper) return null;

      // Check if it's using custom order function
      if ((grouper as any).fn && ((grouper as any).fn as any).fnName === "customOrderGrouper") {
        return "custom";
      }

      // Check ascending/descending
      if (grouper.ascending === false) {
        return "desc";
      }

      return "asc";
    },
    [store, storeUpdateTrigger]
  );

  const sortHeadersByGridOrder = useCallback(
    (items: any[]) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig) return items;
      const orderMap = new Map<string, number>();
      const gridGroupHeadersTitles: string[] = store.groupHeaderRecords.map((groupHeader: any, idx: number) => {

        /* To order by the title of the group headers, we need to get the 'title' but the bryntum group header records don't carry the titles directly we could use .data property to get _groupValue but not using that property is suggested therefore we are relying on the meta property. */
        const title = groupHeader.meta.groupRowFor;
        if (title) {
          orderMap.set(title, idx);
          return title;
        }
      });
      const getOrder = (val: string) => (orderMap.has(val) ? orderMap.get(val)! : gridGroupHeadersTitles.length);
      return [...items].sort((a, b) => getOrder(getGroupHeaderTitle(a)) - getOrder(getGroupHeaderTitle(b)));
    },
    [store, getActiveConfig]
  );

  const allGroupersItems = useMemo(() => {
    if (!isKanbanMode) return [];
    const activeConfig = getActiveConfig(store);
    const grouperItems =
      project?.getCrudStore(activeConfig?.grouperStoreId ?? "")?.query((record: any) => (activeConfig?.grouperStoreFilterFn?.(record) ?? true), true) || [];
    return sortHeadersByGridOrder(grouperItems);
  }, [store, storeUpdateTrigger, kanbanFieldConfig, isKanbanMode, getActiveConfig, project, sortHeadersByGridOrder]);

  const isGroupingApplied = useMemo(() => {
    if (!store?.groupers?.length) return false;
    return store.groupers.some((grouper: any) => kanbanFieldConfig.some((config) => config.grouperField === grouper.field));
  }, [store, storeUpdateTrigger, kanbanFieldConfig]);

  const activeConfig = useMemo(() => getActiveConfig(store), [store, getActiveConfig, isGroupingApplied, storeUpdateTrigger]);

  const isEmptyValue = useCallback(
    (record: any) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig) return null;
      return !(Array.isArray(record[activeConfig.setterField]) ? record[activeConfig.setterField].length > 0 : record[activeConfig.setterField]);
    },
    [store, getActiveConfig]
  );

  const getUngroupedColumnData = useCallback(() => {
    const activeConfig = getActiveConfig(store);
    if (!store || !activeConfig) return [];
    return store.query((record: any) => !record.isGroupHeader && !!isEmptyValue(record)) || [];
  }, [store, getActiveConfig, isEmptyValue]);

  const buildColumns = useCallback(() => {
    const activeConfig = getActiveConfig(store);
    if (!store || !activeConfig || !allGroupersItems?.length) return [];

    const setterField = activeConfig.setterField;
    const result: KanbanColumnType<any>[] = allGroupersItems.map((item: any) => {
      const value = item.id ?? NULL_VALUE;
      const title = getGroupHeaderTitle(item);

      const data =
        store.query((record: any) => {
          if (record.isGroupHeader || record.isLinked) return false;
          const recordValue = record[setterField];
          return Array.isArray(recordValue) ? (recordValue ?? []).includes(value) : recordValue === value;
        }) ?? [];

      return { id: `swivu-kanban-column-${value}`, title, value, data };
    });

    if (store.count > 0 && !activeConfig.isNotNullable) {
      result.unshift({
        id: UNGROUPED_COLUMN_ID,
        title: "N/A",
        value: NULL_VALUE,
        data: getUngroupedColumnData() || []
      });
    }

    return result;
  }, [store, allGroupersItems, getActiveConfig, getUngroupedColumnData]);

  const syncCollapseStateFromGrid = useCallback(() => {
    // Use the store directly from the hook, not from gridRef
    if (!store?.groupHeaderRecords || !activeConfig) {
      return;
    }

    const newCollapsedState: Record<string, boolean> = {};

    // Loop through all grid group headers and get their collapse state
    store.groupHeaderRecords.forEach((groupHeader: any) => {
      // Get the actual value used in kanban columns - from the first child's setter field
      // The kanban columns are built using item.id (numeric), not the display title
      const firstChild = groupHeader.groupChildren?.[0];
      const rawValue = firstChild ? firstChild[activeConfig.setterField] : null;

      // For array fields (like tags), kanban columns are built per individual tag
      // So we need to map each tag to a separate collapsed state entry
      const isCollapsed = groupHeader.meta?.collapsed ?? false;

      if (Array.isArray(rawValue)) {
        // Array field: add collapse state for each value in the array
        if (rawValue.length === 0) {
          // Empty array means N/A group
          if (isCollapsed) {
            newCollapsedState[UNGROUPED_COLUMN_ID] = true;
          }
        } else {
          rawValue.forEach((val: any) => {
            const columnId = val === null ? UNGROUPED_COLUMN_ID : `swivu-kanban-column-${val}`;
            if (isCollapsed) {
              newCollapsedState[columnId] = true;
            }
          });
        }
      } else {
        // Single value field: use the value directly
        const columnId = rawValue === null ? UNGROUPED_COLUMN_ID : `swivu-kanban-column-${rawValue}`;
        if (isCollapsed) {
          newCollapsedState[columnId] = true;
        }
      }
    });

    // Note: N/A column (UNGROUPED_COLUMN_ID) may not exist in grid if it has no records
    // In that case, it won't be in newCollapsedState, which means it will default to expanded
    // This is the correct behavior

    // Update the collapsed columns state
    setCollapsedColumns(newCollapsedState);
  }, [store, activeConfig]);

  const handleOnToggleView = useCallback(
    (viewMode: "kanban" | "grid") => {
      const isKanban = viewMode === "kanban";
      setIsKanbanMode(isKanban);
      const gridInstance = gridRef?.current?.instance;
      if (gridInstance) {
        gridInstance.hidden = isKanban;
        /* There is an issue when reselecting current view from the view menu & state is being applied in the grid again by the useEnableViewTracking hook, that causes the grid to not render properly. I am using this workaround to render the rows properly again after user switches from the kanban view. */
        if (!isKanban) gridInstance.renderContents();

        // Note: Collapse state sync happens automatically in the useEffect when columns are built
      }
    },
    [gridRef]
  );

  const applyGroupingAndOpenKanban = useCallback(
    (grouperField: string, groupOrder?: "asc" | "desc" | "custom") => {
      if (!store) return;

      const config = kanbanFieldConfig.find((c) => c.grouperField === grouperField);

      if (groupOrder === "custom" && config?.customOrderConfig) {
        // Apply custom order grouping
        store.group({
          field: grouperField,
          fn: STORE_GROUPERS.customOrderGrouper(config.customOrderConfig.field, config.customOrderConfig.storeName) as any
        } as any);
      } else if (groupOrder === "desc") {
        // Apply descending order grouping
        store.group({
          field: grouperField,
          ascending: false
        } as any);
      } else {
        // Apply ascending order grouping (default)
        store.group({
          field: grouperField,
          ascending: true
        } as any);
      }

      // Open kanban view if not already in kanban mode
      if (!isKanbanMode) {
        setIsKanbanMode(true);
        const gridInstance = gridRef?.current?.instance;
        if (gridInstance) {
          gridInstance.hidden = true;
        }

        // Note: Collapse state sync happens automatically in the useEffect when columns are built
      }
    },
    [store, gridRef, isKanbanMode, kanbanFieldConfig]
  );

  const handleChange = useCallback((newColumns: KanbanColumnType<any>[]) => {
    setColumns(newColumns);
  }, []);

  const handlePersist = useCallback(
    ({ movedItem, targetColumnValue }: KanbanColumnsPersistPayload<any>) => {
      const activeConfig = getActiveConfig(store);
      if (!activeConfig) return;

      if (movedItem) {
        const isArrayField = Array.isArray(movedItem[activeConfig.setterField]);
        if (isArrayField) {
          if (targetColumnValue === NULL_VALUE) {
            movedItem[activeConfig.setterField] = [];
            return;
          }
          const prevValues = movedItem[activeConfig.setterField];
          const valuesSet = new Set([...prevValues, targetColumnValue]);
          movedItem[activeConfig.setterField] = Array.from(valuesSet);
        } else {
          movedItem[activeConfig.setterField] = targetColumnValue;
        }
      }
    },
    [store, getActiveConfig]
  );

  const toggleColumnCollapse = useCallback((columnValue: string) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [columnValue]: !prev[columnValue]
    }));
  }, []);

  const removeFromCurrentColumn = useCallback(
    (itemRecord: any, columnValue: any) => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig || itemRecord == null) return;

      const isArrayField = Array.isArray(itemRecord[activeConfig.setterField]);
      if (isArrayField) {
        const prevValues = itemRecord[activeConfig.setterField] ?? [];
        itemRecord[activeConfig.setterField] = prevValues.filter((v: any) => v !== columnValue);
      } else {
        itemRecord[activeConfig.setterField] = NULL_VALUE;
      }
    },
    [store, getActiveConfig]
  );

  const getFilteredHiddenCountByColumnId = useCallback(
    (currentColumns: KanbanColumnType<any>[] | null): Record<string, number> => {
      const activeConfig = getActiveConfig(store);
      if (!store || !activeConfig || !currentColumns?.length) return {};

      const setterField = activeConfig.setterField;
      const result: Record<string, number> = {};

      for (const column of currentColumns) {
        const isUngrouped = column.id === UNGROUPED_COLUMN_ID;
        const columnPredicate = isUngrouped
          ? (record: any) => !record.isGroupHeader && !!isEmptyValue(record)
          : (record: any) => {
              if (record.isGroupHeader || record.isLinked) return false;
              const recordValue = record[setterField];
              const value = column.value;
              return Array.isArray(recordValue) ? (recordValue ?? []).includes(value) : recordValue === value;
            };
        const totalInColumn = (store.query(columnPredicate, true) ?? []).length;
        const visibleInColumn = column.data?.length ?? 0;
        const hidden = totalInColumn - visibleInColumn;
        if (hidden > 0) result[column.id] = hidden;
      }

      return result;
    },
    [store, getActiveConfig, isEmptyValue]
  );

  useEffect(() => {
    if (!store) return;
    const detacher = store.on({
      group: () => setStoreUpdateTrigger((prev) => prev + 1)
    });
    return () => detacher();
  }, [store]);

  useEffect(() => {
    if (!store) {
      return;
    }
    if (!isKanbanMode) {
      setColumns(null);
      return;
    }
    const activeConfig = getActiveConfig(store);
    if (!activeConfig) {
      setColumns(null);
      return;
    }
    setColumns(buildColumns());
    if (storeVersion === storeVersionRef.current) {
      syncCollapseStateFromGrid();
    }
    storeVersionRef.current = storeVersion;
  }, [store, storeUpdateTrigger, kanbanFieldConfig, project, isKanbanMode, storeVersion, allGroupersItems, getActiveConfig, buildColumns, syncCollapseStateFromGrid]);

  // Reset to grid mode when the viewManagerState changes
  // This handles both: (1) switching between different views, (2) reselecting the same view from mega menu
  // When a view is selected/reselected, viewManagerState object reference changes
  useEffect(() => {
    // Check if viewManagerState has changed (indicates view selection/reselection)
    if (previousViewManagerStateRef.current !== null && previousViewManagerStateRef.current !== viewManagerState && viewManagerState !== null) {
      // View has been selected/reselected - reset to grid mode
      if (isKanbanMode) {
        setIsKanbanMode(false);
        const gridInstance = gridRef?.current?.instance;
        if (gridInstance) {
          gridInstance.hidden = false;
          // Need to call renderContents to properly re-render the grid after being hidden
          // This fixes rendering issues when switching from kanban back to grid
          gridInstance.renderContents();
        }
      }
    }

    // Update the ref for next comparison
    previousViewManagerStateRef.current = viewManagerState;
  }, [viewManagerState, isKanbanMode, gridRef]);

  return {
    isKanbanMode,
    columns,
    handleOnToggleView,
    isGroupingApplied,
    handleChange,
    handlePersist,
    toggleColumnCollapse,
    collapsedColumns,
    getActiveConfig,
    isEmptyValue,
    removeFromCurrentColumn,
    getFilteredHiddenCountByColumnId,
    activeConfig,
    applyGroupingAndOpenKanban,
    kanbanFieldConfig,
    getActiveGroupOrder
  };
};
